import React, { useEffect } from 'react';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import 'overlayscrollbars/overlayscrollbars.css';
import styled from 'styled-components';
import { useScroll } from '../../contexts/ScrollContext';
import { useTheme } from '../../contexts/ThemeContext';

// Apply global styles to hide native scrollbar
const ScrollWrapper = styled.div`
  /* Hide the native scrollbar for all elements */
  html, body {
    overflow: hidden; /* Prevent native scrolling */
    height: 100%;
    width: 100%;
    margin: 0;
    padding: 0;
  }

  /* Ensure the component takes full viewport */
  height: 100vh;
  width: 100%;
`;

const ScrollContainer = styled(OverlayScrollbarsComponent)`
  height: 100vh;
  width: 100%;

  .os-scrollbar {
    --os-size: 12px;
    --os-padding-perpendicular: 2px;
    --os-padding-axis: 2px;
    --os-track-border-radius: 5px;
    --os-track-bg: ${({ theme }) => `${theme.colors.secondary}30`};
    --os-track-bg-hover: ${({ theme }) => `${theme.colors.secondary}50`};
    --os-track-bg-active: ${({ theme }) => `${theme.colors.secondary}70`};
    --os-track-border: none;
    --os-handle-bg: ${({ theme }) => theme.colors.accent};
    --os-handle-bg-hover: ${({ theme }) => theme.colors.accent};
    --os-handle-bg-active: ${({ theme }) => theme.colors.accent};
    --os-handle-border-radius: 5px;
    --os-handle-border: none;
    --os-handle-min-size: 50px;
    --os-handle-perpendicular-size: 100%;

    .os-scrollbar-handle {
      transition: all 0.2s ease;
    }
  }
`;

const ScrollbarWrapper = ({ children }) => {
  const { setScrollInstance } = useScroll();
  const { isDark } = useTheme();

  // Update scrollbar colors when theme changes
  useEffect(() => {
    const root = document.documentElement;
    
    if (isDark) {
      root.style.setProperty('--os-track-bg', 'rgba(30, 41, 59, 0.3)');
      root.style.setProperty('--os-track-bg-hover', 'rgba(30, 41, 59, 0.5)');
    } else {
      root.style.setProperty('--os-track-bg', 'rgba(226, 232, 240, 0.3)');
      root.style.setProperty('--os-track-bg-hover', 'rgba(226, 232, 240, 0.5)');
    }
    
    // Hide native scrollbar on body
    document.body.style.overflow = 'hidden';
    
    return () => {
      // Restore native scrollbar if component unmounts
      document.body.style.overflow = '';
    };
  }, [isDark]);

  return (
    <ScrollWrapper>
      <ScrollContainer
        options={{
          scrollbars: {
            theme: 'os-theme-custom',
            autoHide: 'scroll',
            autoHideDelay: 400,
          },
          overflow: {
            x: 'hidden',
          }
        }}
        defer
        events={{ 
          initialized: (instance) => {
            setScrollInstance(instance);
            // Add a class to the body to indicate custom scrollbar is active
            document.body.classList.add('custom-scrollbar-active');
          },
          destroyed: () => {
            document.body.classList.remove('custom-scrollbar-active');
          }
        }}
      >
        {children}
      </ScrollContainer>
    </ScrollWrapper>
  );
};

export default ScrollbarWrapper;